import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Program block virtualisation.
//
// Rows cannot be the unit because the program cell spans its rate rows, so the
// block is. Blocks stay inside the one grid rather than being absolutely
// positioned: a spacer grid item above and below the visible range holds the
// scroll height, which keeps every block on the same column tracks. That only
// works while the tracks are content independent, see columns.tsx.
//
// A block's height is the height of its program cell, since that cell spans
// exactly the rows the block owns.

/** Collapsed block height, used until a block has been measured. */
export const ESTIMATED_BLOCK_HEIGHT = 60;

/** Blocks rendered beyond the viewport on each side, to cover fast scrolling. */
const OVERSCAN = 4;

export interface BlockWindow {
  startIndex: number;
  endIndex: number;
  topSpacer: number;
  bottomSpacer: number;
}

/**
 * Visible block range and the spacer heights that stand in for the rest.
 * @param offsets running offset per block, with a final entry for total height
 * @param scrollTop current scroll position of the container
 * @param viewport visible height of the container
 * @param count number of blocks
 * @param overscan blocks to render beyond the viewport on each side
 * @returns the range to render and the spacer heights around it
 */
export const computeWindow = (
  offsets: number[],
  scrollTop: number,
  viewport: number,
  count: number,
  overscan: number,
): BlockWindow => {
  const totalHeight = offsets[count] ?? 0;
  if (count === 0) {
    return { startIndex: 0, endIndex: -1, topSpacer: 0, bottomSpacer: 0 };
  }

  let first = offsets.findIndex((_, i) => (offsets[i + 1] ?? 0) > scrollTop);
  if (first < 0 || first > count - 1) first = count - 1;
  const startIndex = Math.max(0, first - overscan);

  let last = startIndex;
  while (last < count - 1 && offsets[last + 1] < scrollTop + viewport) {
    last += 1;
  }
  const endIndex = Math.min(count - 1, last + overscan);

  return {
    startIndex,
    endIndex,
    topSpacer: offsets[startIndex] ?? 0,
    bottomSpacer: Math.max(
      0,
      totalHeight - (offsets[endIndex + 1] ?? totalHeight),
    ),
  };
};

export interface VirtualBlocks {
  startIndex: number;
  endIndex: number;
  topSpacer: number;
  bottomSpacer: number;
  totalHeight: number;
  measureBlock: (key: number, height: number) => void;
}

export const useVirtualBlocks = (
  scrollRef: React.RefObject<HTMLElement>,
  keys: number[],
  enabled = true,
): VirtualBlocks => {
  const [heights, setHeights] = useState<Record<number, number>>({});
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);
  const pending = useRef<Record<number, number>>({});
  const flushHandle = useRef<number | null>(null);

  // Measurements arrive one block at a time during a single paint. Batching
  // them keeps that from becoming one render per block.
  const measureBlock = useCallback((key: number, height: number) => {
    if (!height) return;
    pending.current[key] = height;
    if (flushHandle.current !== null) return;
    flushHandle.current = window.requestAnimationFrame(() => {
      flushHandle.current = null;
      const batch = pending.current;
      pending.current = {};
      setHeights((prev) => {
        const changed = Object.keys(batch).some(
          (k) => prev[Number(k)] !== batch[Number(k)],
        );
        return changed ? { ...prev, ...batch } : prev;
      });
    });
  }, []);

  useEffect(
    () => () => {
      if (flushHandle.current !== null) {
        window.cancelAnimationFrame(flushHandle.current);
      }
    },
    [],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return undefined;
    const read = () => {
      setScrollTop(el.scrollTop);
      setViewport(el.clientHeight);
    };
    read();
    el.addEventListener('scroll', read, { passive: true });
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', read);
      observer.disconnect();
    };
  }, [scrollRef, enabled]);

  // Running offset per block, so a measured block shifts everything below it.
  const offsets = useMemo(() => {
    const acc: number[] = [];
    let running = 0;
    keys.forEach((key) => {
      acc.push(running);
      running += heights[key] ?? ESTIMATED_BLOCK_HEIGHT;
    });
    acc.push(running);
    return acc;
  }, [keys, heights]);

  const totalHeight = offsets[offsets.length - 1] ?? 0;

  return useMemo(() => {
    if (!enabled || !viewport) {
      return {
        startIndex: 0,
        endIndex: keys.length - 1,
        topSpacer: 0,
        bottomSpacer: 0,
        totalHeight,
        measureBlock,
      };
    }

    return {
      ...computeWindow(offsets, scrollTop, viewport, keys.length, OVERSCAN),
      totalHeight,
      measureBlock,
    };
  }, [
    enabled,
    viewport,
    scrollTop,
    offsets,
    keys.length,
    totalHeight,
    measureBlock,
  ]);
};
